import { useCallback, useMemo } from 'react';
import { UnitsWeight } from 'harmony-constants';
import { L } from 'harmony-language';
import { useUser } from '../../../api/queries/use-user';
import { WeightConversions } from 'harmony-constants/lib/src/db/constants';

const WeightAbbreviation = {
    'USTon': L.unitsImperialWeightLargeAbbr(),
    'Pound': L.unitsImperialWeightAbbr(),
    'MetricTon': L.unitsMetricWeightLargeAbbr(),
    'Kilogram': L.unitsMetricWeightAbbr(),
}

const WeightAbbreviationPerMin = {
    'USTon': L.weightTonPerMin(),
    'Pound': L.weightPoundPerMin(),
    'MetricTon': L.weightMetricTonPerMin(),
    'Kilogram': L.weightKilogramMin(),
}

export const convert = (from: keyof typeof UnitsWeight | 'gram', to: keyof typeof UnitsWeight | 'gram', precision: number) => (amount: number) => {
    // @ts-ignore
    const base = amount * WeightConversions[`${from.toLowerCase()}->${to.toLowerCase()}`];
    return Number(base.toFixed(precision));
}

export const useWeights = () => {
    const { user } = useUser();
    const weightSystem = user.contact?.unitsWeight || user.organization?.unitsWeight || UnitsWeight.Pound;

    const weightAbbreviation = WeightAbbreviation[weightSystem];

    const isDecimalWeight = weightSystem === UnitsWeight.MetricTon || weightSystem === UnitsWeight.USTon;
    const weightPrecision = isDecimalWeight ? 2 : 0;

    const convertFromGrams = useCallback((grams: number) => {
        return convert('gram', weightSystem, weightPrecision)(grams);
    }, [weightSystem]);

    const convertToGrams = useCallback((value: number) => {
        return convert(weightSystem, 'gram', 0)(value);
    }, [weightSystem]);

    const convertFromGramsDisplay = useCallback((grams: number) => {
        const userDefinedNumber = convert('gram', weightSystem, weightPrecision)(grams);
        if (isDecimalWeight) {
            return userDefinedNumber.toFixed(2).concat(' ', weightAbbreviation);
        } else {
            return userDefinedNumber.toString().concat(' ', weightAbbreviation);
        }
    }, [weightSystem]);

    const stringAbbreviationDisplay = useMemo(() => {
        return WeightAbbreviationPerMin[weightSystem];
    }, [weightSystem]);

    return {
        weightSystem,
        weightAbbreviation,
        weightPrecision,
        convertFromGrams,
        convertToGrams,
        convertFromGramsDisplay,
        stringAbbreviationDisplay,
        isDecimalWeight,
    }
};
